import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { RootLayout } from './components/layout/RootLayout'
import { Loading } from './components/ui/Loading'
import { RouteErrorBoundary, isChunkLoadError, StaleChunkFallback } from './components/RouteErrorBoundary'

/**
 * A rejected dynamic `import()` does not reliably surface as a catchable
 * render error through React.lazy/Suspense in this app (verified — neither a
 * router errorElement nor a class error boundary around Suspense ever fired;
 * the page just hung on the Suspense fallback forever). So a stale-chunk
 * failure is intercepted directly on the loader promise, before it ever
 * reaches `lazy()`, and resolved to a friendly fallback component instead of
 * being left to reject. `RouteErrorBoundary` still wraps the result as a
 * catch-all for ordinary bugs in an already-loaded page.
 *
 * Deliberately does NOT auto-reload (see RouteErrorBoundary's comment) — a
 * chunk that 404s every time (a genuinely broken deploy, not just this tab
 * being stale) must never be able to trigger a reload loop.
 */
function page(loader: () => Promise<{ default: () => ReactNode }>) {
  const Lazy = lazy(() =>
    loader().catch((err: unknown) => {
      if (!isChunkLoadError(err)) throw err
      return { default: StaleChunkFallback }
    }),
  )
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<Loading label="Loading…" className="py-24" />}>
        <Lazy />
      </Suspense>
    </RouteErrorBoundary>
  )
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: page(() => import('./features/home/HomePage')) },
      { path: 'how-to-play', element: page(() => import('./features/rules/HowToPlayPage')) },
      { path: 'build', element: page(() => import('./features/builder/CharacterBuilderPage')) },
      { path: 'play', element: page(() => import('./features/simulator/SimulatorPage')) },
      { path: 'legal/:doc', element: page(() => import('./features/legal/LegalPage')) },
      { path: 'store', element: page(() => import('./features/store/StorePage')) },
      { path: 'auth/verify', element: page(() => import('./features/auth/VerifyPage')) },
      {
        path: '*',
        element: (
          <div className="mx-auto max-w-xl px-4 py-24 text-center">
            <h1 className="text-3xl font-black uppercase">Not Found</h1>
            <p className="mt-2 text-white/60">This page doesn't exist.</p>
          </div>
        ),
      },
    ],
  },
])
